import React from 'react';
import {
  Grid, Typography, Card, CardContent, Box, Button, Alert,
  FormControl, Select, MenuItem, Chip, TextField, IconButton,
  Paper, CircularProgress, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow
} from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DownloadIcon from '@mui/icons-material/Download';
import { isRewardComingSoon } from '../../utils/rewardComingSoon';

/** `cardRequested` in `user_reward_providers.config` JSON for the current user's row */
function getEdenredCardRequestedCount(userRewardProviderRow) {
  const cfg = userRewardProviderRow?.config;
  if (!cfg || typeof cfg !== 'object') return 0;
  const raw = cfg.cardRequested ?? cfg.card_requested;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
}

/**
 * RewardsSection – Renders all reward providers (GiftMall, Edenred, Tremendous),
 * cart summary, pending orders, and My Vouchers.
 * Extracted from Dealer.js to keep the campaign detail view manageable.
 */
const RewardsSection = ({
  selectedCampaignDetail,
  showRewards,
  rewardsLocked = false,
  rewardsLockedReason = null,
  CDN_URL,
  t,
  formatDateTime,
  // Cart props
  cart, cartDenominations, setCartDenominations,
  addToCart, removeFromCart, getCartTotal, clearCart,
  getTotalItemsInCart,
  // Edenred props
  edenredCart, edenredCustomValues, setEdenredCustomValues,
  getEdenredCartTotal,
  calculateEdenredTransferPoints,
  // Tremendous props
  tremendousCustomValues, setTremendousCustomValues,
  requestingTremendousVoucher,
  handleRequestTremendousVoucherClick,
  // Voucher requests
  requestingVouchers, handleRequestVouchersClick,
  handleTransferMoneyClick,
  // Voucher items
  voucherItems, loadingVoucherItems, voucherItemsError,
  // Pending requests
  pendingVoucherRequests,
  // User reward config (Edenred)
  userRewardProviderConfig, setUserRewardProviderConfig, loadingUserConfig,
  selectedSerialNumber, setSelectedSerialNumber,
  requestingCard, setRequestingCard,
  // Helpers
  getAvailablePoints,
  // API
  apiCall,
  showFeedback,
}) => {
  if (!showRewards) return null;

  return (
    <Grid container spacing={3} sx={{ width: '100%', overflow: 'visible' }}>
      {rewardsLocked && (
        <Grid item xs={12} sx={{ pl: 0 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {rewardsLockedReason === 'min_value_1'
              ? t('campaigns.tournamentRewardsLockedMinValue1', {
                  count: 5,
                  value1Label: selectedCampaignDetail?.value_1_label || t('dashboard.value1'),
                })
              : t('campaigns.tournamentRewardsLockedDisclaimer')}
          </Alert>
        </Grid>
      )}
      <Grid item xs={12} sx={{ pl: 0, width: '100%' }}>
        <fieldset
          disabled={rewardsLocked}
          style={{
            border: 'none',
            margin: 0,
            padding: 0,
            minWidth: 0,
            width: '100%',
          }}
        >
          <Box
            sx={(theme) => ({
              pointerEvents: rewardsLocked ? 'none' : undefined,
              opacity: rewardsLocked ? 0.72 : 1,
              ...(rewardsLocked && {
                filter: 'grayscale(1)',
                '& .MuiCard-root': {
                  bgcolor:
                    theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100],
                  border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
                  boxShadow: 'none',
                },
                '& .MuiPaper-root': {
                  backgroundColor: `${theme.palette.grey[100]} !important`,
                  borderColor: `${theme.palette.grey[400]} !important`,
                  boxShadow: 'none',
                },
                '& .MuiButton-contained': {
                  backgroundImage: 'none',
                  boxShadow: 'none',
                  backgroundColor: `${theme.palette.grey[500]} !important`,
                  color: `${theme.palette.grey[50]} !important`,
                  '& .MuiSvgIcon-root, & svg': {
                    fill: `${theme.palette.grey[50]} !important`,
                  },
                },
                '& .MuiButton-outlined': {
                  borderColor: theme.palette.grey[500],
                  color: theme.palette.grey[700],
                  backgroundColor: theme.palette.grey[100],
                  '& .MuiSvgIcon-root': {
                    color: theme.palette.grey[700],
                  },
                },
                '& .MuiIconButton-root': {
                  backgroundColor: `${theme.palette.grey[400]} !important`,
                  color: `${theme.palette.grey[900]} !important`,
                  border: `1px solid ${theme.palette.grey[500]} !important`,
                  '&:disabled': {
                    backgroundColor: `${theme.palette.grey[300]} !important`,
                    color: theme.palette.grey[600],
                  },
                  '& svg': {
                    fill: `${theme.palette.grey[900]} !important`,
                  },
                  '& .MuiSvgIcon-root': {
                    color: theme.palette.grey[900],
                  },
                },
                '& .MuiChip-colorPrimary.MuiChip-outlined': {
                  borderColor: theme.palette.grey[500],
                  color: theme.palette.grey[700],
                },
                '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${theme.palette.grey[400]} !important`,
                },
              }),
            })}
          >
            <Grid container spacing={3} sx={{ width: '100%', overflow: 'visible' }}>
      {/* Available Rewards */}
      <Grid item xs={12} sx={{ pl: 0 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          {t('campaigns.availableRewards')}
        </Typography>

        {selectedCampaignDetail.rewards && selectedCampaignDetail.rewards.length > 0 ? (
          (() => {
            // Group rewards by provider
            const rewardsByProvider = {};
            selectedCampaignDetail.rewards.forEach(reward => {
              const providerName = reward.reward_provider?.name || 'Unknown';
              if (!rewardsByProvider[providerName]) {
                rewardsByProvider[providerName] = [];
              }
              rewardsByProvider[providerName].push(reward);
            });

            const edenredRewards = [];
            const tremendousRewards = [];
            const giftMallGroups = {};

            Object.entries(rewardsByProvider).forEach(([providerName, rewards]) => {
              const providerNameLower = providerName.toLowerCase();
              const isEdenred = providerNameLower.includes('edenred');
              const isTremendous = providerNameLower.includes('tremendous');

              if (isEdenred) {
                edenredRewards.push(...rewards);
              } else if (isTremendous) {
                tremendousRewards.push(...rewards);
              } else {
                const firstReward = rewards[0];
                const providerDisplayName = firstReward?.reward_provider?.display_name || firstReward?.reward_provider?.name || providerName;
                if (!giftMallGroups[providerDisplayName]) {
                  giftMallGroups[providerDisplayName] = [];
                }
                giftMallGroups[providerDisplayName].push(...rewards);
              }
            });

            const giftMallRewards = Object.entries(giftMallGroups).map(([providerName, rewards]) => ({
              providerName,
              rewards
            }));

            return (
              <>
                {/* GiftMall Providers Section */}
                {giftMallRewards.map(({ providerName, rewards }) => (
                  <Box key={providerName} sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                      {providerName}
                    </Typography>
                    <Grid container spacing={1.5}>
                      {rewards
                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                        .map((reward) => {
                          const comingSoon = isRewardComingSoon(reward);
                          return (
                          <Grid item xs={6} sm={4} md={3} lg={2} key={reward.id}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', opacity: comingSoon ? 0.72 : 1 }}>
                              <Box sx={{ p: 1.5, textAlign: 'center' }}>
                                {reward.reward_image ? (
                                  <img
                                    src={`${CDN_URL}/${reward.reward_image}`}
                                    alt={reward.name}
                                    style={{ width: '100px', height: '70px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #e0e0e0' }}
                                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                  />
                                ) : null}
                                <Box sx={{ width: '100px', height: '70px', display: reward.reward_image ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', borderRadius: '6px', border: '1px solid #e0e0e0', margin: '0 auto' }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>No Image</Typography>
                                </Box>
                              </Box>
                              <CardContent sx={{ flexGrow: 1, pt: 0, pb: 1.5, px: 1.5 }}>
                                {comingSoon && (
                                  <Alert severity="info" sx={{ mb: 1, py: 0.25, fontSize: '0.7rem', '& .MuiAlert-message': { width: '100%' } }}>
                                    {t('campaigns.rewardAvailableSoon')}
                                  </Alert>
                                )}
                                <Typography variant="subtitle2" component="div" gutterBottom sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                                  {reward.name}
                                </Typography>
                                {reward.description && (
                                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontSize: '0.75rem' }}>
                                    {reward.description}
                                  </Typography>
                                )}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
                                  {(() => {
                                    if (comingSoon) return null;
                                    const denoms = reward.denominations && reward.denominations.length > 0
                                      ? reward.denominations
                                      : (reward.denomination ? [{ value: reward.denomination, currency: reward.currency || 'USD', is_default: true }] : []);
                                    const defaultDenom = denoms.find(d => d.is_default) || denoms[0];
                                    const selectedDenom = cartDenominations[reward.id] || defaultDenom;
                                    const currency = selectedDenom?.currency || defaultDenom?.currency || reward.currency || 'USD';

                                    if (reward.any_value) {
                                      return (
                                        <TextField
                                          size="small" type="number" label="Value (points)"
                                          value={selectedDenom?.value ?? ''}
                                          onChange={(e) => {
                                            const val = parseFloat(e.target.value);
                                            setCartDenominations(prev => ({ ...prev, [reward.id]: { value: isNaN(val) ? 0 : val, currency } }));
                                          }}
                                          inputProps={{ min: 0, step: 1 }}
                                          sx={{ fontSize: '0.7rem', '& .MuiInputBase-input': { fontSize: '0.7rem', height: '28px', py: 0.5 } }}
                                        />
                                      );
                                    }
                                    if (denoms.length > 1) {
                                      return (
                                        <Select
                                          size="small"
                                          value={selectedDenom ? `${selectedDenom.value}-${selectedDenom.currency}` : ''}
                                          onChange={(e) => {
                                            const [value, curr] = e.target.value.split('-');
                                            setCartDenominations(prev => ({ ...prev, [reward.id]: { value: parseFloat(value), currency: curr } }));
                                          }}
                                          sx={{ fontSize: '0.7rem', height: '28px', minWidth: '100px',
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' }
                                          }}
                                        >
                                          {denoms.map((denom, idx) => (
                                            <MenuItem key={idx} value={`${denom.value}-${denom.currency}`}>{`${denom.value} points`}</MenuItem>
                                          ))}
                                        </Select>
                                      );
                                    } else if (selectedDenom) {
                                      return <Chip label={`${selectedDenom.value} points`} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem', height: '24px' }} />;
                                    }
                                    return null;
                                  })()}
                                  {reward.reward_provider && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                      {reward.reward_provider.display_name || reward.reward_provider.name}
                                    </Typography>
                                  )}
                                </Box>

                                {/* Cart Controls */}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 'auto' }}>
                                  <IconButton size="small" onClick={() => removeFromCart(reward.id)} disabled={!cart[reward.id]}
                                    sx={{ width: 24, height: 24, backgroundColor: 'white', border: '1px solid #e0e0e0', '&:disabled': { borderColor: '#f5f5f5' } }}>
                                    <RemoveIcon sx={{ fontSize: '12px' }} />
                                  </IconButton>
                                  <Typography variant="caption" sx={{ minWidth: '20px', textAlign: 'center', fontWeight: 500, fontSize: '0.75rem' }}>
                                    {cart[reward.id] || 0}
                                  </Typography>
                                  <IconButton size="small"
                                    onClick={() => {
                                      const denoms = reward.denominations && reward.denominations.length > 0
                                        ? reward.denominations
                                        : (reward.denomination ? [{ value: reward.denomination, currency: reward.currency || 'USD', is_default: true }] : []);
                                      const defaultDenom = denoms.find(d => d.is_default) || denoms[0];
                                      const currency = reward.currency || defaultDenom?.currency || 'USD';
                                      const selectedDenom = cartDenominations[reward.id] || (reward.any_value ? { value: 0, currency } : defaultDenom);
                                      const denomValue = selectedDenom?.value || 0;
                                      if (!cartDenominations[reward.id] && selectedDenom) {
                                        setCartDenominations(prev => ({ ...prev, [reward.id]: selectedDenom }));
                                      }
                                      addToCart(reward.id, denomValue);
                                    }}
                                    disabled={comingSoon || (!reward.any_value && !reward.denomination && (!reward.denominations || reward.denominations.length === 0)) ||
                                      (() => {
                                        const denoms = reward.denominations && reward.denominations.length > 0
                                          ? reward.denominations
                                          : (reward.denomination ? [{ value: reward.denomination, currency: reward.currency || 'USD', is_default: true }] : []);
                                        const defaultDenom = denoms.find(d => d.is_default) || denoms[0];
                                        const selectedDenom = cartDenominations[reward.id] || defaultDenom;
                                        const denomValue = selectedDenom?.value || 0;
                                        if (reward.any_value && denomValue <= 0) return true;
                                        return (getCartTotal() + denomValue) > getAvailablePoints();
                                      })()
                                    }
                                    sx={{ width: 24, height: 24, backgroundColor: 'success.main', border: '1px solid #4caf50', color: 'white',
                                      '&:hover': { backgroundColor: 'success.dark' },
                                      '&:disabled': { backgroundColor: 'white', borderColor: '#f5f5f5', color: 'text.disabled' }
                                    }}>
                                    <AddIcon sx={{ fontSize: '12px' }} />
                                  </IconButton>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                          );
                        })}
                    </Grid>
                  </Box>
                ))}

                {/* Cart Summary */}
                {giftMallRewards.length > 0 && (
                  <Box sx={{ mt: 3, mb: 4 }}>
                    <Paper sx={{ p: 3, backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ShoppingCartIcon sx={{ fontSize: '20px' }} />
                        {t('campaigns.cartSummary')}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('campaigns.availablePoints')}: <strong>{getAvailablePoints()}</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('campaigns.itemsInCart')} <strong>{getTotalItemsInCart()}</strong>
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {t('campaigns.totalCost')} {getCartTotal()} {t('campaigns.points')}
                        </Typography>
                        <Typography variant="body2" color={(getCartTotal() + getEdenredCartTotal()) > getAvailablePoints() ? 'error.main' : 'success.main'}>
                          {t('campaigns.remaining')} {getAvailablePoints() - getCartTotal() - getEdenredCartTotal()} {t('campaigns.points')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                        <Button variant="outlined" onClick={clearCart} disabled={getTotalItemsInCart() === 0} sx={{ minWidth: '120px' }}>
                          {t('campaigns.clearCart')}
                        </Button>
                        <Button variant="contained" onClick={handleRequestVouchersClick}
                          disabled={getTotalItemsInCart() === 0 || (getCartTotal() + getEdenredCartTotal()) > getAvailablePoints() || requestingVouchers}
                          startIcon={requestingVouchers ? <CircularProgress size={16} /> : <ShoppingCartIcon />}
                          sx={{ minWidth: '150px' }}>
                          {requestingVouchers ? t('campaigns.requesting') : t('campaigns.requestVouchers')}
                        </Button>
                      </Box>
                    </Paper>
                  </Box>
                )}

                {/* Tremendous Section */}
                {tremendousRewards.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                      {tremendousRewards[0]?.reward_provider?.display_name || tremendousRewards[0]?.reward_provider?.name || 'Tremendous'}
                    </Typography>
                    <Grid container spacing={2}>
                      {tremendousRewards.sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((reward) => {
                        const comingSoon = isRewardComingSoon(reward);
                        const denoms = reward.denominations && reward.denominations.length > 0
                          ? reward.denominations
                          : (reward.denomination ? [{ value: reward.denomination, currency: reward.currency || 'USD', is_default: true }] : []);
                        const defaultDenom = denoms.find(d => d.is_default) || denoms[0];
                        const defaultValue = defaultDenom?.value || reward.denomination || 50;
                        const denomsWithCode = denoms.filter(d => d.code && String(d.code).trim());
                        const hasFixedDenominations = !reward.any_value && denomsWithCode.length > 0;
                        const fixedDefaultValue = hasFixedDenominations ? (denomsWithCode[0]?.value ?? defaultValue) : defaultValue;
                        const customValue = tremendousCustomValues[reward.id] !== undefined ? tremendousCustomValues[reward.id] : fixedDefaultValue;
                        const currencyLabel = defaultDenom?.currency || reward.currency || 'USD';
                        const effectiveValue = reward.any_value ? customValue : (hasFixedDenominations && denomsWithCode.length === 1 ? denomsWithCode[0].value : customValue);
                        return (
                          <Grid item xs={12} sm={6} md={4} key={reward.id}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', opacity: comingSoon ? 0.72 : 1 }}>
                              <Box sx={{ p: 1.5, textAlign: 'center' }}>
                                {reward.reward_image ? (
                                  <img src={`${CDN_URL}/${reward.reward_image}`} alt={reward.name}
                                    style={{ width: '100px', height: '70px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #e0e0e0' }}
                                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                                ) : null}
                                <Box sx={{ width: '100px', height: '70px', display: reward.reward_image ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', borderRadius: '6px', border: '1px solid #e0e0e0', margin: '0 auto' }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>No Image</Typography>
                                </Box>
                              </Box>
                              <CardContent sx={{ flexGrow: 1, pt: 0, pb: 1.5, px: 1.5 }}>
                                {comingSoon && (
                                  <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
                                    {t('campaigns.rewardAvailableSoon')}
                                  </Alert>
                                )}
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>{reward.name}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                  {reward.description || 'Request a reward voucher'}
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  {!comingSoon && (
                                    reward.any_value ? (
                                      <TextField label={`Value (${currencyLabel})`} type="number" size="small" value={customValue}
                                        onChange={(e) => {
                                          const numericValue = parseFloat(e.target.value) || 0;
                                          const newValue = Math.max(0, numericValue);
                                          setTremendousCustomValues(prev => ({ ...prev, [reward.id]: newValue }));
                                        }}
                                        inputProps={{ min: 0, step: 0.01 }} fullWidth
                                        helperText={`Default: ${defaultValue} ${currencyLabel}`}
                                      />
                                    ) : hasFixedDenominations ? (
                                      denomsWithCode.length > 1 ? (
                                        <FormControl size="small" fullWidth>
                                          <Select
                                            labelId={`tremendous-denom-${reward.id}`}
                                            id={`tremendous-denom-select-${reward.id}`}
                                            value={(customValue === undefined || customValue === '' ? denomsWithCode[0]?.value : customValue)}
                                            onChange={(e) => {
                                              const val = parseFloat(e.target.value) || 0;
                                              setTremendousCustomValues(prev => ({ ...prev, [reward.id]: val }));
                                            }}
                                            displayEmpty
                                            renderValue={(v) => {
                                              if (v === '' || v === undefined || (typeof v === 'number' && isNaN(v))) return 'Select amount';
                                              const d = denomsWithCode.find(x => Math.abs(Number(x.value) - Number(v)) < 0.01);
                                              return d ? `${d.value} ${d.currency || currencyLabel}` : `${v} ${currencyLabel}`;
                                            }}
                                          >
                                            {denomsWithCode.map((d, idx) => (
                                              <MenuItem key={idx} value={d.value}>{d.value} {d.currency || currencyLabel}</MenuItem>
                                            ))}
                                          </Select>
                                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>Select amount ({currencyLabel})</Typography>
                                        </FormControl>
                                      ) : (
                                        <Typography variant="body1" sx={{ py: 1, fontWeight: 500 }}>
                                          {denomsWithCode[0].value} {denomsWithCode[0].currency || currencyLabel}
                                        </Typography>
                                      )
                                    ) : (
                                      <TextField label={`Value (${currencyLabel})`} type="number" size="small" value={customValue}
                                        onChange={(e) => {
                                          const numericValue = parseFloat(e.target.value) || 0;
                                          const newValue = Math.max(0, numericValue);
                                          setTremendousCustomValues(prev => ({ ...prev, [reward.id]: newValue }));
                                        }}
                                        inputProps={{ min: 0, step: 0.01 }} fullWidth
                                        helperText={`Default: ${defaultValue} ${currencyLabel}`}
                                      />
                                    )
                                  )}
                                  <Button variant="contained" color="primary" fullWidth
                                    onClick={() => handleRequestTremendousVoucherClick(reward.id)}
                                    disabled={comingSoon || requestingTremendousVoucher || effectiveValue <= 0 || effectiveValue > getAvailablePoints()}
                                    startIcon={requestingTremendousVoucher ? <CircularProgress size={16} /> : null}>
                                    {requestingTremendousVoucher ? t('campaigns.requesting') : t('campaigns.requestVoucher')}
                                  </Button>
                                  {!comingSoon && effectiveValue > getAvailablePoints() && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                      {t('campaigns.insufficientPointsAvailable')} {getAvailablePoints()}
                                    </Typography>
                                  )}
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                )}
                {/* Edenred Section */}
                {edenredRewards.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    {(() => {
                      const serialCount = (userRewardProviderConfig?.config?.serialNumbers?.length || 0);
                      // Order section: visible while fewer than 3 serial numbers in config (ignore cardRequested for visibility).
                      const showRequestCardBox = !loadingUserConfig && serialCount < 3;
                      return (
                        <>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                              {edenredRewards[0]?.reward_provider?.display_name || edenredRewards[0]?.reward_provider?.name || 'Edenred_PL'}
                            </Typography>
                            <Grid container spacing={2} alignItems="flex-start">
                              <Grid item xs={12} md={showRequestCardBox ? 4 : 12} lg={showRequestCardBox ? 4 : 12}>
                                <Grid container spacing={2}>
                                  {edenredRewards.sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((reward) => {
                                    const comingSoon = isRewardComingSoon(reward);
                                    const denoms = reward.denominations && reward.denominations.length > 0
                                      ? reward.denominations
                                      : (reward.denomination ? [{ value: reward.denomination, currency: reward.currency || 'USD', is_default: true }] : []);
                                    const defaultDenom = denoms.find(d => d.is_default) || denoms[0];
                                    const defaultValue = defaultDenom?.value || reward.denomination || 0;
                                    const customValue = comingSoon ? 0 : (edenredCustomValues[reward.id] !== undefined ? edenredCustomValues[reward.id] : defaultValue);
                                    return (
                                      <Grid item xs={12} sm={showRequestCardBox ? 12 : 6} md={showRequestCardBox ? 12 : 4} key={reward.id}>
                                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', opacity: comingSoon ? 0.72 : 1 }}>
                                          <Box sx={{ p: 1.5, textAlign: 'center' }}>
                                            {reward.reward_image ? (
                                              <img src={`${CDN_URL}/${reward.reward_image}`} alt={t('campaigns.edenredCardTitleUnderLogo')}
                                                style={{ width: '100px', height: '70px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #e0e0e0' }}
                                                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                                            ) : null}
                                            <Box sx={{ width: '100px', height: '70px', display: reward.reward_image ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', borderRadius: '6px', border: '1px solid #e0e0e0', margin: '0 auto' }}>
                                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>No Image</Typography>
                                            </Box>
                                          </Box>
                                          <CardContent sx={{ flexGrow: 1, pt: 0, pb: 1.5, px: 1.5 }}>
                                            {comingSoon && (
                                              <Alert severity="info" sx={{ mb: 1, py: 0.25, fontSize: '0.7rem', '& .MuiAlert-message': { width: '100%' } }}>
                                                {t('campaigns.rewardAvailableSoon')}
                                              </Alert>
                                            )}
                                            <Typography variant="subtitle2" component="div" gutterBottom sx={{ fontWeight: 500, fontSize: '0.875rem' }}>{t('campaigns.edenredCardTitleUnderLogo')}</Typography>
                                            {!loadingUserConfig && serialCount === 0 ? (
                                              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontSize: '0.75rem' }}>
                                                {t('campaigns.edenredPleaseOrderCardBelow')}
                                              </Typography>
                                            ) : reward.description ? (
                                              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontSize: '0.75rem' }}>{reward.description}</Typography>
                                            ) : null}
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
                                              {loadingUserConfig ? <CircularProgress size={20} /> : (
                                                <FormControl size="small" fullWidth>
                                                  {userRewardProviderConfig?.config?.serialNumbers?.length > 0 ? (
                                                    <Select value={selectedSerialNumber} onChange={(e) => setSelectedSerialNumber(e.target.value)} displayEmpty>
                                                      {userRewardProviderConfig.config.serialNumbers.map((serial) => (
                                                        <MenuItem key={serial} value={serial}>{serial}</MenuItem>
                                                      ))}
                                                    </Select>
                                                  ) : (
                                                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>{t('campaigns.edenredNoCardTopUpHint')}</Typography>
                                                  )}
                                                </FormControl>
                                              )}
                                              {!comingSoon && (
                                                <>
                                                  <TextField label={t('campaigns.value')} type="number" size="small" value={customValue}
                                                    onChange={(e) => {
                                                      const inputValue = e.target.value;
                                                      const integerValue = inputValue.includes('.') ? parseInt(inputValue.split('.')[0]) || 0 : parseInt(inputValue) || 0;
                                                      const newValue = Math.max(0, integerValue);
                                                      setEdenredCustomValues(prev => ({ ...prev, [reward.id]: newValue }));
                                                      setCartDenominations(prev => ({ ...prev, [reward.id]: { value: newValue, currency: defaultDenom?.currency || reward.currency || 'USD' } }));
                                                    }}
                                                    inputProps={{ min: 0, step: 1 }} sx={{ fontSize: '0.7rem' }}
                                                  />
                                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                    {t('campaigns.default')} {defaultValue} {defaultDenom?.currency || reward.currency || 'USD'}
                                                  </Typography>
                                                </>
                                              )}
                                            </Box>
                                            <Box sx={{ mt: 2 }}>
                                              <Button variant="contained" color="primary" fullWidth onClick={handleTransferMoneyClick}
                                                disabled={!selectedSerialNumber || calculateEdenredTransferPoints() <= 0
                                                  || (getCartTotal() + calculateEdenredTransferPoints()) > getAvailablePoints() || requestingVouchers}
                                                startIcon={requestingVouchers ? <CircularProgress size={16} /> : null}>
                                                {requestingVouchers ? t('campaigns.transferring') : t('campaigns.transferMoney')}
                                              </Button>
                                              {(getCartTotal() + calculateEdenredTransferPoints()) > getAvailablePoints() && (
                                                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                                  {t('campaigns.insufficientPointsAvailable')} {getAvailablePoints()}
                                                </Typography>
                                              )}
                                            </Box>
                                          </CardContent>
                                        </Card>
                                      </Grid>
                                    );
                                  })}
                                </Grid>
                              </Grid>

                              {/* Request Edenred Card — to the right of transfer when fewer than 3 serials */}
                              {showRequestCardBox && (
                                <Grid item xs={12} md={8} lg={8}>
                                  <Grid container spacing={2} sx={{ alignItems: 'flex-start' }}>
                                    <Grid item xs={12}>
                                      <Paper sx={{ p: 3, backgroundColor: '#fff3cd', border: '1px solid #ffc107', height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>{t('campaigns.requestEdenredCard')}</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.875rem' }}>
                                          {t('campaigns.numberOfOrderedCards')} {getEdenredCardRequestedCount(userRewardProviderConfig)} / 3
                                        </Typography>
                                        <Button variant="contained" color="warning" fullWidth
                                          onClick={async () => {
                                            if (!edenredRewards.length || !edenredRewards[0]?.reward_provider?.id) return;
                                            setRequestingCard(true);
                                            try {
                                              const currentConfig = userRewardProviderConfig?.config || {};
                                              const newCardRequested = getEdenredCardRequestedCount(userRewardProviderConfig) + 1;
                                              const result = await apiCall('put', `/api/my-reward-providers/${edenredRewards[0].reward_provider.id}`, {
                                                config: { ...currentConfig, cardRequested: newCardRequested }
                                              });
                                              if (result.success) {
                                                if (result.data && setUserRewardProviderConfig) setUserRewardProviderConfig(result.data);
                                                if (showFeedback) {
                                                  showFeedback('success', t('errors.cardRequestSuccess'), '');
                                                } else {
                                                  alert(t('errors.cardRequestSuccess'));
                                                }
                                              } else if (showFeedback) {
                                                showFeedback('error', t('dialogs.errorNoticeTitle'), result.error || t('errors.failedRequestCard'));
                                              } else {
                                                alert(result.error || t('errors.failedRequestCard'));
                                              }
                                            } catch (error) {
                                              console.error('Error requesting card:', error);
                                              if (showFeedback) {
                                                showFeedback('error', t('dialogs.errorNoticeTitle'), t('errors.failedRequestCard'));
                                              } else {
                                                alert(t('errors.failedRequestCard'));
                                              }
                                            }
                                            finally { setRequestingCard(false); }
                                          }}
                                          disabled={requestingCard || getEdenredCardRequestedCount(userRewardProviderConfig) >= 3}
                                          startIcon={requestingCard ? <CircularProgress size={16} /> : <CreditCardIcon />}>
                                          {requestingCard ? t('campaigns.requesting') : t('campaigns.requestEdenredCard')}
                                        </Button>
                                      </Paper>
                                    </Grid>
                                    <Grid item xs={12}>
                                      <Box
                                        sx={{
                                          fontSize: '0.875rem',
                                          lineHeight: 1.6,
                                          color: 'text.secondary',
                                          '& p': { margin: '0 0 12px' },
                                          '& p:last-child': { marginBottom: 0 },
                                          '& strong u': { textDecoration: 'underline' },
                                        }}
                                        dangerouslySetInnerHTML={{ __html: t('campaigns.edenredCardOrderInfoHtml') }}
                                      />
                                    </Grid>
                                  </Grid>
                                </Grid>
                              )}
                            </Grid>
                        </>
                      );
                    })()}
                  </Box>
                )}

              </>
            );
          })()
        ) : (
          <Alert severity="info">{t('campaigns.noRewardsAvailable')}</Alert>
        )}
      </Grid>

      {/* Pending Orders Section */}
      {pendingVoucherRequests && pendingVoucherRequests.length > 0 && (
        <Grid item xs={12}>
          <Alert severity="info" icon={<CircularProgress size={20} />} sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>{t('campaigns.processingOrders')}</Typography>
            <Typography variant="body2">{t('campaigns.processingOrdersDescription')}</Typography>
          </Alert>
          <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', mb: 4 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('campaigns.orderId')}</TableCell>
                  <TableCell>{t('campaigns.items')}</TableCell>
                  <TableCell>{t('campaigns.totalCost')}</TableCell>
                  <TableCell>{t('campaigns.status')}</TableCell>
                  <TableCell>{t('campaigns.date')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingVoucherRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>#{request.order_number || request.id}</Typography>
                    </TableCell>
                    <TableCell>
                      {request.vouchers && request.vouchers.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {request.vouchers.map((voucher, idx) => (
                            <Typography key={idx} variant="body2">{voucher.reward_name} × {voucher.quantity}</Typography>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">{t('campaigns.na')}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={`${request.total_cost} points`} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip label={t('campaigns.processing')} size="small" color="warning" icon={<CircularProgress size={12} />} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{formatDateTime(request.created)}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      )}

      {/* My Vouchers Section */}
      <Grid item xs={12} sx={{ pl: 0 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          {t('campaigns.myVouchers')}
        </Typography>

        {voucherItemsError && (
          <Alert severity="error" sx={{ mb: 3 }}>{voucherItemsError}</Alert>
        )}

        {loadingVoucherItems ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : voucherItems && voucherItems.length > 0 ? (
          <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('campaigns.rewardName')}</TableCell>
                  <TableCell>{t('campaigns.denomination')}</TableCell>
                  <TableCell>{t('campaigns.voucherLink')}</TableCell>
                  <TableCell>{t('campaigns.date')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {voucherItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.reward_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={`${item.denomination} points`} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {item.product_id === 'edenred' ? (
                        <Typography variant="body2" color="text.secondary">
                          {t('campaigns.edenredTransferNoDownloadLink')}
                        </Typography>
                      ) : (
                        <Button variant="outlined" size="small" href={item.voucher_url} target="_blank" rel="noopener noreferrer"
                          startIcon={<DownloadIcon />} sx={{ minWidth: '120px' }}>
                          {t('campaigns.getVoucher')}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{formatDateTime(item.created)}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="info">{t('campaigns.noVouchersYet')}</Alert>
        )}
      </Grid>
            </Grid>
          </Box>
        </fieldset>
      </Grid>
    </Grid>
  );
};

export default RewardsSection;
